/**
 * feature/analysis — spec §F6, the analysis toolbox; Amendment 1 §1/§A gave it a three-tab
 * shell.
 *
 * "Right dock (390px, draggable header, ✕, '← All tools' back), home = intro line (+
 * AOI-active note) + 6 tool cards (icon tile colour per Part H) + 'Clear result layers
 * (n)'. Buffer (§H7) and overlay (§H8) left the dock in Task 10 (client items 11/12) —
 * UI-only, their §D6 routes and tool modules stay registered. Tool run: params form →
 * POST → progress bar polling job → result panels (tables
 * `.result-box`, charts, buttons) → optional result layers on map (registered in legend
 * block (e)). Errors → toast + inline message."
 *
 * Markup and class names are the mockup's `#analysisDock` so the dock is pixel-identical;
 * what is not the mockup's:
 *
 * * **it is mounted only while it is open.** The mockup toggled a `.show` class on a node
 *   that lived for the life of the page, which kept a finished result on screen for the
 *   whole session. Here, closing the dock unmounts the running tool — which stops its poll,
 *   the one thing that must not outlive the panel (a timer against a throttled endpoint).
 *   A run whose panel is closed keeps going server-side and its result stays in the §E7
 *   cache, so re-running the same parameters answers instantly; §F5.6's block (e) is where
 *   a *result* persists, which is the part a reader actually needs to keep.
 * * **the header drags through `lib/draggable.ts`**, like every other floating widget
 *   (§F5.11), rather than being fixed to the mockup's `top:12px;right:56px`. The open flag
 *   is passed to the hook as `enabled`: this component is mounted while it is closed and
 *   renders `null`, so without it the drag effect would run once at page load against a
 *   `null` element and never again — a header that looks grabbable and is not (NOTES #637).
 * * **each tool panel renders inside its own `ErrorBoundary`.** A panel dereferences the
 *   result while it renders — `result.rows.length`, `result.matrix.classes` — and a shape it
 *   did not expect is a TypeError that would otherwise unmount the React root and leave a
 *   blank portal (NOTES #650). Keyed on the tool id, so switching tools clears a boundary
 *   that has already caught: the reader gets the next tool's form, not the last one's error.
 *
 * ## Amendment 1: three tabs, all three bodies always mounted (§1/§G.1)
 *
 * `#intelTabs` (`Tabs.tsx`) sits under the header; `intel.tab` says which of the three
 * `#intelPanel-*` bodies is showing, the other two carrying `hidden` rather than being
 * unmounted — "switching tabs preserves state: a half-configured tool under My Analysis
 * survives a peek at Help; the selected use case survives a trip to My Analysis." That is
 * also why the My Analysis tool switch moved out of this component's own `useState` into
 * `intel.dockTool` (Task 6's `tool`): a Help card's "Take me there" has to be able to open a
 * tool from the Help panel, a sibling this component's local state could never reach.
 *
 * The old `#intelSelect` "Analysis tools" option is gone — the dropdown under Use Cases now
 * lists only use cases (`UseCaseSelect`), and the three tabs are what switches between the
 * pane and the toolbox.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDraggable } from '../../lib/draggable';
import { Icon } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { ToolId } from '../../lib/analysis';
import { Help } from '../intelligence/Help';
import { IntelPane } from '../intelligence';
import { Tabs } from '../intelligence/Tabs';
import { UseCaseSelect } from '../intelligence/UseCaseSelect';
import { ToolHome } from './ToolHome';
import { Compare } from './tools/Compare';
import { Aoi } from './tools/Aoi';
import { Zonal } from './tools/Zonal';
import { Suitability } from './tools/Suitability';
import { Change } from './tools/Change';
import { Spi } from './tools/Spi';

/** What every tool component is handed: how to get back to the six cards. */
export interface ToolProps {
  onBack: () => void;
}

// `Partial`, not `Record<ToolId, …>`: `'conditions'` (Task 7) is a `ToolId` for
// `useToolRun`'s sake, but it is reached only from UC3's own stepper
// (`ConditionBuilder`), never from `ToolHome`'s card grid or `intel.dockTool` — so it has no
// view registered here at all.
const TOOL_VIEWS: Partial<Record<ToolId, (props: ToolProps) => JSX.Element>> = {
  compare: Compare,
  aoi: Aoi,
  zonal: Zonal,
  suitability: Suitability,
  change: Change,
  spi: Spi,
};

export function AnalysisDock() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.analysisOpen);
  const setOpen = useAppStore((s) => s.setAnalysisOpen);
  const tab = useAppStore((s) => s.intel.tab);
  const useCase = useAppStore((s) => s.intel.useCase);
  const dockTool = useAppStore((s) => s.intel.dockTool);
  const setDockTool = useAppStore((s) => s.setIntelDockTool);

  const dockRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLElement | null>(null);
  useDraggable(dockRef, headRef, { enabled: open });

  if (!open) return null;

  const View = dockTool ? TOOL_VIEWS[dockTool] : null;

  return (
    <div id="analysisDock" ref={dockRef}>
      <header ref={headRef}>
        <Icon name="wrench" size={16} />
        <h3>{t('analysis.title')}</h3>
        <button
          type="button"
          id="adClose"
          onClick={() => setOpen(false)}
          aria-label={t('widget.close')}
          title={t('widget.close')}
        >
          ×
        </button>
      </header>
      <Tabs />
      <div id="adBody">
        {/* Tab 1 — Use Cases (default). All three panels below stay mounted for the life of
            the open dock; only `hidden` says which one is on screen (§1's "switching tabs
            preserves state"). */}
        <div
          id="intelPanel-usecases"
          role="tabpanel"
          aria-labelledby="intelTab-usecases"
          hidden={tab !== 'usecases'}
        >
          <h4 className="intel-usecases-heading">{t('intel.tabs.usecasesHeading')}</h4>
          <UseCaseSelect />
          <ErrorBoundary variant="panel" key={useCase ?? 'none'}>
            <IntelPane />
          </ErrorBoundary>
        </div>

        {/* Tab 2 — My Analysis: today's `ToolHome`/tool switch, unchanged in function, only
            relocated and re-homed on `intel.dockTool` instead of a local `useState`. */}
        <div
          id="intelPanel-tools"
          role="tabpanel"
          aria-labelledby="intelTab-tools"
          hidden={tab !== 'tools'}
        >
          {View ? (
            <ErrorBoundary variant="panel" key={dockTool}>
              <View onBack={() => setDockTool(null)} />
            </ErrorBoundary>
          ) : (
            <ToolHome onOpen={setDockTool} />
          )}
        </div>

        {/* Tab 3 — Help. */}
        <div id="intelPanel-help" role="tabpanel" aria-labelledby="intelTab-help" hidden={tab !== 'help'}>
          <ErrorBoundary variant="panel">
            <Help />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

export default AnalysisDock;
